import type React from 'react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import type { ApplicationData } from '@xing-com/crate-jobs-helpers';
import { useApplicationTypeHelpers } from '@xing-com/crate-jobs-hooks';
import { IconApply } from '@xing-com/icons';
import { mediaConfined } from '@xing-com/layout-tokens';

import { useApplyButtonStateProps } from './apply-button-state-context';
import { useApplyButtonDefaultProps } from './use-apply-button-default-props';
import { useApplyButtonEmailApplyProps } from './use-apply-button-email-apply-props';
import { useApplyButtonMessageApplyProps } from './use-apply-button-message-apply-props';
import { useApplyButtonUrlApplyProps } from './use-apply-button-url-apply-props';

type Props = {
  application: ApplicationData;
  isHeaderSticky?: boolean;
  variant?: 'primary' | 'secondary';
  iconOnly?: boolean;
  className?: string;
  trackingProps?: Record<string, unknown>;
};

const Apply = styled(Button)<{ $isHeaderSticky: boolean }>`
  width: 100%;

  @media ${mediaConfined} {
    flex-basis: ${({ $isHeaderSticky }) => ($isHeaderSticky ? 'auto' : '46%')};
  }
`;

export const ApplyButton: React.FC<Props> = ({
  application,
  isHeaderSticky = false,
  variant = 'primary',
  iconOnly,
  className,
  trackingProps,
}) => {
  const intl = useIntl();
  const {
    type,
    applyUrl,
    applyEmail,
    contactUserId,
    code,
    jobId,
    prioritized,
    slug,
    title,
  } = application;

  const {
    isXingApplication,
    isCustomApplication,
    isEmailApplication,
    isMessageApplication,
    isUrlApplication,
  } = useApplicationTypeHelpers(type);

  const label = useMemo(() => {
    if (isXingApplication || isCustomApplication) {
      return 'JOBS_JOB_DETAILS_XING_APPLY';
    }
    if (isUrlApplication) {
      return 'JOB_POSTING_APPLY_EXTERNAL_URL';
    }
    return 'JOBS_JOB_DETAILS_APPLY';
  }, [isXingApplication, isCustomApplication, isUrlApplication]);

  const defaultProps = useApplyButtonDefaultProps(slug);
  const applyFlowProps = useApplyButtonStateProps();
  const messageApplyProps = useApplyButtonMessageApplyProps({
    contactUserId,
    jobId,
    trackingProps,
  });
  const urlApplyProps = useApplyButtonUrlApplyProps({
    jobId,
    slug,
    applyUrl,
    isPrioritizedJob: prioritized,
    trackingProps,
  });
  const emailApplyProps = useApplyButtonEmailApplyProps({
    code,
    applyEmail,
    title,
    jobId,
    trackingProps,
  });
  const additionalProps = {
    ...defaultProps,
    ...(isEmailApplication && emailApplyProps),
    ...(isMessageApplication && messageApplyProps),
    ...(isUrlApplication && urlApplyProps),
    ...((isXingApplication || isCustomApplication) && applyFlowProps),
  };

  const handleOnClick = (event: React.MouseEvent): void => {
    event.stopPropagation();

    if (isXingApplication || isCustomApplication) {
      applyFlowProps.onClick(trackingProps);
    } else {
      additionalProps.onClick?.();
    }
  };

  return (
    <Apply
      variant={variant}
      size="medium"
      aria-label={intl.formatMessage({ id: label })}
      $isHeaderSticky={isHeaderSticky}
      className={className}
      {...additionalProps}
      icon={iconOnly ? IconApply : additionalProps.icon}
      onClick={handleOnClick}
    >
      {!iconOnly && <FormattedMessage id={label} />}
    </Apply>
  );
};
